import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import {
  Syringe, AlertTriangle, CheckCircle, Droplets, Info
} from "lucide-react";

export function PeptidesTab({ peptides }) {
  return (
    <div className="space-y-6" data-testid="peptides-content">
      {peptides?.map((peptide, index) => (
        <Card key={peptide.name || index} className="peptide-card overflow-hidden" data-testid={`peptide-card-${index}`}>
          <CardHeader className="border-b border-zinc-800/50 pb-4">
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-cyan-500/20 to-violet-500/20 flex items-center justify-center">
                  <Syringe className="w-6 h-6 text-cyan-400" />
                </div>
                <div>
                  <CardTitle className="text-xl text-white">{peptide.name}</CardTitle>
                  <p className="text-sm text-zinc-400">{peptide.description}</p>
                </div>
              </div>
              <Badge className="badge-cyan">{peptide.frequency}</Badge>
            </div>
          </CardHeader>
          <CardContent className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <div className="space-y-4">
                <h4 className="text-sm font-medium text-zinc-400 uppercase tracking-wider">Dosage</h4>
                <div className="space-y-2">
                  {peptide.dosage_mg && (
                    <div className="flex items-center justify-between bg-zinc-900/50 rounded-lg px-4 py-3">
                      <span className="text-zinc-400">Per Dose</span>
                      <span className="text-xl font-bold text-cyan-400 font-mono">{peptide.dosage_mg} mg</span>
                    </div>
                  )}
                  {peptide.dosage_iu && (
                    <div className="flex items-center justify-between bg-zinc-900/50 rounded-lg px-4 py-3">
                      <span className="text-zinc-400">Per Dose</span>
                      <span className="text-xl font-bold text-cyan-400 font-mono">{peptide.dosage_iu} IU</span>
                    </div>
                  )}
                  <div className="flex items-center justify-between bg-zinc-900/50 rounded-lg px-4 py-3">
                    <span className="text-zinc-400">Timing</span>
                    <span className="text-white font-medium text-right text-sm">{peptide.timing}</span>
                  </div>
                </div>
              </div>

              <div className="space-y-4">
                <h4 className="text-sm font-medium text-zinc-400 uppercase tracking-wider">Cycle Info</h4>
                <div className="space-y-2">
                  <div className="flex items-center justify-between bg-zinc-900/50 rounded-lg px-4 py-3">
                    <span className="text-zinc-400">Duration</span>
                    <span className="text-white font-bold">{peptide.cycle_duration_weeks} weeks</span>
                  </div>
                  <div className="flex items-center justify-between bg-zinc-900/50 rounded-lg px-4 py-3">
                    <span className="text-zinc-400">Vials Needed</span>
                    <span className="text-white font-bold">{peptide.vials_needed} vials</span>
                  </div>
                </div>
              </div>

              <div className="space-y-4">
                <h4 className="text-sm font-medium text-zinc-400 uppercase tracking-wider">Cost</h4>
                <div className="space-y-2">
                  <div className="flex items-center justify-between bg-zinc-900/50 rounded-lg px-4 py-3">
                    <span className="text-zinc-400">Per Vial</span>
                    <span className="text-white font-bold">${peptide.cost_per_vial_usd}</span>
                  </div>
                  <div className="flex items-center justify-between bg-gradient-to-r from-green-500/10 to-transparent rounded-lg px-4 py-3 border border-green-500/20">
                    <span className="text-zinc-400">Total Cost</span>
                    <span className="text-2xl font-bold text-green-400">${peptide.total_cost_usd}</span>
                  </div>
                </div>
              </div>
            </div>

            {peptide.reconstitution && (
              <>
                <Separator className="my-6 bg-zinc-800" />
                <div className="bg-gradient-to-br from-cyan-500/5 to-violet-500/5 rounded-xl p-6 border border-cyan-500/20">
                  <h4 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                    <Droplets className="w-5 h-5 text-cyan-400" />
                    Mixing Instructions for {peptide.name}
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
                    <div className="bg-zinc-900/50 rounded-lg p-3 text-center">
                      <p className="text-xs text-zinc-500 uppercase mb-1">BAC Water</p>
                      <p className="text-xl font-bold text-cyan-400">{peptide.reconstitution.bac_water_ml} ml</p>
                    </div>
                    <div className="bg-zinc-900/50 rounded-lg p-3 text-center">
                      <p className="text-xs text-zinc-500 uppercase mb-1">Vial Size</p>
                      <p className="text-xl font-bold text-violet-400">{peptide.reconstitution.vial_size_mg} mg</p>
                    </div>
                    <div className="bg-zinc-900/50 rounded-lg p-3 text-center">
                      <p className="text-xs text-zinc-500 uppercase mb-1">Concentration</p>
                      <p className="text-lg font-bold text-green-400">{peptide.reconstitution.concentration_per_ml}</p>
                    </div>
                    <div className="bg-zinc-900/50 rounded-lg p-3 text-center">
                      <p className="text-xs text-zinc-500 uppercase mb-1">Dose Per</p>
                      <p className="text-sm font-bold text-amber-400">{peptide.reconstitution.dosage_per_tick}</p>
                    </div>
                  </div>
                  <div className="space-y-2">
                    {peptide.reconstitution.mixing_instructions?.map((step, i) => (
                      <div key={`step-${i}-${step.slice(0, 20)}`} className="flex items-start gap-3 text-sm">
                        <div className="w-5 h-5 rounded-full bg-cyan-500/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                          <span className="text-xs text-cyan-400">{i + 1}</span>
                        </div>
                        <p className="text-zinc-300">{step}</p>
                      </div>
                    ))}
                  </div>
                  <div className="mt-4 flex items-center gap-2 text-sm text-zinc-400">
                    <Syringe className="w-4 h-4" />
                    <span>Recommended: {peptide.reconstitution.syringe_type}</span>
                  </div>
                </div>
              </>
            )}

            <Separator className="my-6 bg-zinc-800" />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h4 className="text-sm font-medium text-zinc-400 uppercase tracking-wider mb-3 flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-400" />
                  Benefits
                </h4>
                <div className="flex flex-wrap gap-2">
                  {peptide.benefits?.map((benefit) => (
                    <Badge key={benefit} className="badge-green">{benefit}</Badge>
                  ))}
                </div>
              </div>
              <div>
                <h4 className="text-sm font-medium text-zinc-400 uppercase tracking-wider mb-3 flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4 text-amber-400" />
                  Potential Side Effects
                </h4>
                <div className="flex flex-wrap gap-2">
                  {peptide.side_effects?.map((effect) => (
                    <Badge key={effect} variant="outline" className="border-amber-500/30 text-amber-400">{effect}</Badge>
                  ))}
                </div>
              </div>
            </div>

            {peptide.notes && (
              <div className="mt-6 p-4 bg-zinc-900/50 rounded-lg border border-zinc-800">
                <div className="flex items-start gap-3">
                  <Info className="w-5 h-5 text-cyan-400 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-zinc-300">{peptide.notes}</p>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      ))}
    </div>
  );
}
